import { useNavigate } from "react-router-dom";

interface Example {
  syntax: string;
  description: string;
}

const EXAMPLES: Example[] = [
  { syntax: "canine animated", description: "AND — posts with both tags" },
  { syntax: "-cat", description: "Exclude posts with this tag" },
  { syntax: "~wolf ~fox", description: "OR — posts with at least one of the ~ group" },
  { syntax: "rating:s", description: "Safe rating (s / q / e)" },
  { syntax: "type:webm", description: "File type filter (webm / gif / apng)" },
  { syntax: "score:>100", description: "Score greater than 100" },
  { syntax: "score:<50", description: "Score less than 50" },
  { syntax: "score:10..50", description: "Score range 10–50" },
  { syntax: "favcount:>20", description: "Fav count greater than 20" },
  { syntax: "width:>1920", description: "Width greater than 1920px" },
  { syntax: "height:>1080", description: "Height greater than 1080px" },
  { syntax: "duration:>5", description: "Duration longer than 5 seconds" },
  { syntax: "id:12345", description: "Exact post ID lookup" },
  { syntax: "md5:abc123", description: "Exact MD5 hash lookup" },
  { syntax: "date:2024-01-01..2024-12-31", description: "Upload date range (YYYY-MM-DD)" },
  { syntax: "order:score", description: "Sort by score descending" },
  { syntax: "order:id_desc", description: "Sort by newest first (default)" },
  { syntax: "order:random", description: "Random order" },
  { syntax: "artist:some_artist", description: "Category-scoped: artist tag" },
  { syntax: "character:name", description: "Category-scoped: character tag" },
  { syntax: "species:canine", description: "Category-scoped: species tag" },
  { syntax: "copyright:series", description: "Category-scoped: copyright tag" },
  { syntax: '"multi word tag"', description: "Tags containing spaces (quoted)" },
];

interface Props {
  compact?: boolean;
}

export function SyntaxHelp({ compact = false }: Props) {
  const navigate = useNavigate();

  return (
    <div className={compact ? "text-sm" : ""}>
      {!compact && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">Search Syntax</h1>
          <p className="text-zinc-400">
            GIF Search uses a tag-based query language modeled after e621. Combine operators
            freely; all tags are searched against a local Postgres index for fast, exact matching.
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-surface-3">
              <th className="pb-2 pr-6 text-zinc-400 font-semibold text-xs uppercase tracking-wider whitespace-nowrap">Syntax</th>
              <th className="pb-2 text-zinc-400 font-semibold text-xs uppercase tracking-wider">Description</th>
            </tr>
          </thead>
          <tbody>
            {EXAMPLES.map((ex) => (
              <tr key={ex.syntax} className="border-b border-surface-2 group">
                <td className="py-2 pr-6 align-top whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <code className="text-accent-light font-mono text-xs bg-surface-2 px-2 py-0.5 rounded">
                      {ex.syntax}
                    </code>
                    <button
                      onClick={() => navigate(`/search?q=${encodeURIComponent(ex.syntax)}`)}
                      className="text-[10px] text-zinc-600 hover:text-accent transition-colors opacity-0 group-hover:opacity-100"
                      title="Try this query"
                    >
                      Try →
                    </button>
                  </div>
                </td>
                <td className="py-2 text-zinc-300 text-sm">{ex.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!compact && (
        <div className="mt-6 p-4 bg-surface-1 rounded-lg border border-surface-3">
          <h2 className="font-semibold mb-2 text-sm">Combining operators</h2>
          <p className="text-zinc-400 text-sm">
            Operators can be combined freely:{" "}
            <code className="text-accent-light font-mono text-xs bg-surface-2 px-1.5 py-0.5 rounded">
              canine ~wolf ~fox -cat rating:e score:&gt;50 order:score
            </code>
          </p>
          <p className="text-zinc-500 text-xs mt-2">
            The safety filter and your personal blacklist are always applied and cannot be overridden.
          </p>
        </div>
      )}
    </div>
  );
}
